import Spinner from "@/components/Spinner";
import {
  HypertensionClinicalProtocol,
  PersonalizedHypertensionClinicalProtocol,
} from "@/model/ClinicalProtocol";
import { revalidateLogic, useForm, useStore } from "@tanstack/react-form";
import { isMatch } from "lodash";
import Button from "../../components/Button";
import { SelectInput, TextInput } from "../../components/InputFields";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/chadcn/Dialog";

type FormValues = {
  clinicalProtocolId: string;
  low: string;
  elevated: string;
  critical: string;
};

type UpdateHypertensionProtocolDialogProps = {
  personalizedClinicalProtocol: PersonalizedHypertensionClinicalProtocol;
  diagnosis?: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  isLoading: boolean;
  isError: boolean;
  onSubmit: (values: FormValues) => Promise<void>;
  organizationClinicalProtocols: HypertensionClinicalProtocol[];
};

export const UpdateHypertensionProtocolDialog = ({
  personalizedClinicalProtocol,
  organizationClinicalProtocols,
  diagnosis,
  open,
  onOpenChange,
  isError: isSubmitError,
  onSubmit,
}: UpdateHypertensionProtocolDialogProps) => {
  const onSubmitInternal = async ({ value }: { value: FormValues }) => await onSubmit({ ...value });

  const initialValues: FormValues = {
    clinicalProtocolId: personalizedClinicalProtocol.organizationClinicalProtocolId,
    ...personalizedClinicalProtocol.thresholds,
  };

  const form = useForm({
    defaultValues: initialValues,
    onSubmit: onSubmitInternal,
    validationLogic: revalidateLogic(),
  });

  const canSubmit = useStore(form.store, ({ values, canSubmit }) => {
    const hasChanges = !isMatch(initialValues, values);
    return hasChanges && canSubmit;
  });

  const handleOnOpenChange = (open: boolean) => {
    form.reset();
    onOpenChange(open);
  };

  return (
    <Dialog open={open} onOpenChange={handleOnOpenChange}>
      <DialogContent className="flex w-[600px] flex-col overflow-hidden bg-white">
        <DialogHeader className="mb-6">
          <span className="flex flex-row items-center gap-4">
            <DialogTitle className="mb-1 truncate text-3xl font-bold">Adjust protocol</DialogTitle>
            <form.Field
              name="clinicalProtocolId"
              children={(field) => (
                <SelectInput
                  className="w-fit"
                  id="protocol-select"
                  value={field.state.value}
                  options={organizationClinicalProtocols.map((it) => ({
                    value: it.id,
                    title: it.name,
                  }))}
                  onChange={(protocolId) => {
                    const selectedProtocol = organizationClinicalProtocols.find(
                      (p) => p.id === protocolId
                    );
                    if (selectedProtocol) {
                      const newValues: FormValues = {
                        clinicalProtocolId: selectedProtocol.id,
                        ...selectedProtocol.thresholds,
                      };
                      form.reset(newValues);
                      field.handleChange(protocolId);
                    }
                  }}
                />
              )}
            />
          </span>
          <div className="flex flex-row justify-between">
            {diagnosis && <p className="truncate">Diagnosis: {diagnosis}</p>}
          </div>
        </DialogHeader>
        <form
          className="flex flex-col"
          onSubmit={(e) => {
            e.preventDefault();
            e.stopPropagation();
            form.handleSubmit();
          }}
        >
          <div className="mb-6 grid grid-cols-[1fr] gap-x-10 gap-y-1">
            <div className="flex flex-col">
              <h1 className="mb-2 text-lg font-semibold">Thresholds</h1>
              <div className="grid grid-cols-[auto_1fr] items-center gap-x-4 gap-y-1">
                <label className="py-px">Low:</label>
                <form.Field
                  name="low"
                  children={(field) => (
                    <TextInput
                      name={field.name}
                      disabled
                      className="w-24 px-2 py-px"
                      value={field.state.value ?? ""}
                      onBlur={() => field.handleBlur()}
                    />
                  )}
                />

                <span className="py-px">Elevated:</span>
                <form.Field
                  name="elevated"
                  children={(field) => (
                    <TextInput
                      name={field.name}
                      disabled
                      className="w-24 px-2 py-px"
                      value={field.state.value ?? ""}
                      onBlur={() => field.handleBlur()}
                    />
                  )}
                />

                <span className="py-px">Critical:</span>
                <form.Field
                  name="critical"
                  children={(field) => (
                    <TextInput
                      name={field.name}
                      disabled
                      className="w-24 px-2 py-px"
                      value={field.state.value ?? ""}
                      onBlur={() => field.handleBlur()}
                    />
                  )}
                />
              </div>
            </div>
          </div>
          <p className="mb-4 italic">Note: Updated protocol will apply only to new measurements</p>
          {isSubmitError && (
            <p className="text-red-600">
              There was an error submitting the new values. Please contact Bloomlife Support, or try
              again later.
            </p>
          )}
          <form.Subscribe
            selector={(state) => state.isSubmitting}
            children={(isSubmitting) => (
              <DialogFooter>
                <Button variant="primary" type="submit" disabled={!canSubmit} className="w-fit">
                  Change
                  {isSubmitting && <Spinner className="ms-2 size-4 border-2" />}
                </Button>
              </DialogFooter>
            )}
          />
        </form>
      </DialogContent>
    </Dialog>
  );
};
